import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import ContentCard from "@/components/feature/ContentCard";
import Pagination from "@/components/base/Pagination";
import { useSeo } from "@/hooks/useSeo";
import {
  categoryMetas,
  contentItems,
  filmGenres,
  filmRegions,
  filmYears,
  musicGenres,
  newsTags,
  type ContentType,
} from "@/mocks/content";
import FilterBar, { type FilterGroup } from "./components/FilterBar";

const PAGE_SIZE = 6;

const seoMap: Record<ContentType, { title: string; description: string; keywords: string }> = {
  film: {
    title: "影视分类 - 快猫",
    description: "快猫影视分类汇集科幻、悬疑、剧情、爱情、奇幻等多类型影视作品，提供封面、简介与热度评分，助你快速发现值得一看的佳作。",
    keywords: "影视分类,快猫影视,科幻电影,悬疑剧,影视推荐",
  },
  music: {
    title: "音乐分类 - 快猫",
    description: "快猫音乐分类涵盖流行、民谣、电子、爵士、说唱、摇滚等丰富风格，推介热门专辑与新声作品，满足不同口味。",
    keywords: "音乐分类,快猫音乐,新曲推荐,热门专辑,音乐风格",
  },
  news: {
    title: "娱乐资讯 - 快猫",
    description: "快猫娱乐资讯聚合影视、音乐与行业最新动态与实时报道，用最短时间掌握娱乐热点。",
    keywords: "娱乐资讯,快猫资讯,行业动态,影视新闻,音乐新闻",
  },
};

const sortOptions = [
  { key: "latest", label: "按最新" },
  { key: "hot", label: "按最热" },
  { key: "rating", label: "按评分" },
];

const daysAgo = (dateStr: string) => (Date.now() - new Date(dateStr).getTime()) / 86400000;

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const valid = type === "film" || type === "music" || type === "news";
  const ctype = (valid ? type : "film") as ContentType;
  const meta = categoryMetas.find((m) => m.type === ctype)!;

  const [loading, setLoading] = useState(true);
  const [genre, setGenre] = useState("全部");
  const [year, setYear] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [musicGenre, setMusicGenre] = useState("全部");
  const [tag, setTag] = useState("全部");
  const [timeRange, setTimeRange] = useState("全部");
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);

  useSeo(seoMap[ctype]);

  useEffect(() => {
    setLoading(true);
    setGenre("全部");
    setYear("全部");
    setRegion("全部");
    setMusicGenre("全部");
    setTag("全部");
    setTimeRange("全部");
    setSort("latest");
    setPage(1);
    const timer = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(timer);
  }, [ctype]);

  const filtered = useMemo(() => {
    let list = contentItems.filter((i) => i.type === ctype);
    if (ctype === "film") {
      if (genre !== "全部") list = list.filter((i) => i.genre === genre);
      if (year !== "全部") list = list.filter((i) => i.year === year);
      if (region !== "全部") list = list.filter((i) => i.region === region);
    } else if (ctype === "music") {
      if (musicGenre !== "全部") list = list.filter((i) => i.musicGenre === musicGenre);
    } else if (ctype === "news") {
      if (tag !== "全部") list = list.filter((i) => i.tags.includes(tag));
      if (timeRange === "最近一周") list = list.filter((i) => daysAgo(i.date) <= 7);
      if (timeRange === "最近一月") list = list.filter((i) => daysAgo(i.date) <= 30);
    }

    const sorted = [...list];
    if (sort === "latest") sorted.sort((a, b) => (a.date < b.date ? 1 : -1));
    if (sort === "hot") sorted.sort((a, b) => b.heat - a.heat);
    if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    return sorted;
  }, [ctype, genre, year, region, musicGenre, tag, timeRange, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setGenre("全部");
    setYear("全部");
    setRegion("全部");
    setMusicGenre("全部");
    setTag("全部");
    setTimeRange("全部");
    setSort("latest");
    setPage(1);
  };

  const groups: FilterGroup[] = [];
  if (ctype === "film") {
    groups.push(
      { key: "genre", label: "类型", options: filmGenres, value: genre, onChange: (v) => { setGenre(v); setPage(1); } },
      { key: "year", label: "年代", options: filmYears, value: year, onChange: (v) => { setYear(v); setPage(1); } },
      { key: "region", label: "地区", options: filmRegions, value: region, onChange: (v) => { setRegion(v); setPage(1); } },
    );
  } else if (ctype === "music") {
    groups.push({
      key: "musicGenre",
      label: "流派",
      options: musicGenres,
      value: musicGenre,
      onChange: (v) => { setMusicGenre(v); setPage(1); },
    });
  } else {
    groups.push(
      { key: "tag", label: "标签", options: newsTags, value: tag, onChange: (v) => { setTag(v); setPage(1); } },
      { key: "time", label: "时间", options: ["全部", "最近一周", "最近一月"], value: timeRange, onChange: (v) => { setTimeRange(v); setPage(1); } },
    );
  }

  if (!valid) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-8">
          <i className="ri-error-warning-line text-5xl text-primary-400" />
          <h1 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950">分类不存在</h1>
          <p className="mt-2 text-sm text-foreground-600">你访问的分类页面可能已调整。</p>
          <Link
            to="/category"
            className="mt-6 rounded-full bg-primary-500 px-6 py-2.5 font-label text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            返回分类汇总
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8 md:py-8">
        <Breadcrumb items={[{ label: "分类", to: "/category" }, { label: meta.subtitle }]} />

        <header className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
              <i className={`${meta.icon} text-2xl`} />
            </span>
            <div>
              <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
                {meta.name}
              </h1>
              <p className="mt-1 text-sm text-foreground-600">{meta.intro}</p>
            </div>
          </div>
          <span className="rounded-full bg-secondary-100 px-3 py-1 font-label text-xs font-medium text-secondary-800">
            共 {filtered.length} 条内容
          </span>
        </header>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
          <aside className="flex flex-col gap-4">
            <FilterBar groups={groups} />
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="font-label text-sm font-medium text-foreground-500">排序</span>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="flex items-center gap-1 text-xs font-medium text-accent-600 hover:text-accent-700"
                >
                  <i className="ri-refresh-line" />
                  重置筛选
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => { setSort(opt.key); setPage(1); }}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 font-label text-sm transition-colors ${
                      sort === opt.key
                        ? "bg-accent-500 text-background-50"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <div>
            {loading ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-72 animate-pulse rounded-lg bg-background-100" />
                ))}
              </div>
            ) : pageItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 py-20 text-center">
                <i className="ri-inbox-line text-5xl text-foreground-300" />
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-800">没有匹配的内容</h3>
                <p className="mt-1 text-sm text-foreground-600">试试调整筛选条件，或一键重置查看全部。</p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-5 flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 font-label text-sm font-semibold text-background-50 hover:bg-primary-600"
                >
                  <i className="ri-refresh-line" />
                  重置筛选
                </button>
              </div>
            ) : (
              <>
                <div className={ctype === "news" ? "flex flex-col gap-4" : "grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"}>
                  {pageItems.map((item) => (
                    <ContentCard key={item.id} item={item} />
                  ))}
                </div>
                <Pagination page={page} totalPages={totalPages} onChange={setPage} />
              </>
            )}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}